<template>
    <teleport to="body">
        <button
            ref="rootRef"
            class="fixed md:top-36 top-[87px] md:right-10 right-4 z-[10000] px-3 py-2 md:px-4 md:py-3 rounded-full text-white shadow-lg border-white/30 border md:border-0"
            :aria-label="isPlaying ? 'Musik pausieren' : 'Musik abspielen'"
            @click="toggleMusic"
        >
            <audio ref="musicRef" :src="$asset(music, 'public')" loop preload="auto" playsinline />

            <div class="flex items-center justify-center">
                <div class="wave-arch relative w-[50px] h-[24px] md:w-[100px] md:h-[30px] overflow-hidden">
                    <!-- Desktop-Wellen -->
                    <span
                        v-for="(h, i) in waveHeights"
                        :key="`d-${i}`"
                        :class="['hidden md:block', 'bar', 'absolute', 'bottom-0', 'w-[4px]', 'bg-current', 'rounded-[2px]', { 'is-playing': isPlaying }]"
                        :style="{
                            left: `${i * 8}px`,
                            height: `${h}px`,
                            animationDelay: `${i * 80}ms`,
                            opacity: 0.95
                        }"
                    />

                    <!-- Mobile-Wellen -->
                    <span
                        v-for="(h, i) in waveHeights"
                        :key="`m-${i}`"
                        :class="['md:hidden', 'bar', 'absolute', 'bottom-0', 'w-[3px]', 'bg-current', 'rounded-[2px]', { 'is-playing': isPlaying }]"
                        :style="{
                            left: `${i * 8}px`,
                            height: `${Math.max(h - 4, 8)}px`,
                            animationDelay: `${i * 80}ms`,
                            opacity: 0.95
                        }"
                    />
                </div>
            </div>
        </button>
    </teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineExpose } from 'vue';

defineProps({
    music: { type: String, default: '/hkcm/music/christmasBackgroundMusic.mp3' }
});

const musicRef = ref(null);
const rootRef = ref(null);

const isPlaying = ref(false);
const stoppedManually = ref(false);

const defaultVolume = 0.1;
const quietVolume = 0.02;
const isQuiet = ref(false);

const waveHeights = [10, 18, 24, 16, 28, 20, 14, 22, 12, 26, 18];

let observer = null;

function applyVolume() {
    const a = musicRef.value;

    if (!a) return;

    a.volume = isQuiet.value ? quietVolume : defaultVolume;
}

function playMusic() {
    const a = musicRef.value;

    if (!a) return;

    a.muted = false;
    applyVolume();
    a.play()
        .then(() => {
            isPlaying.value = true;
        })
        .catch(() => {
            isPlaying.value = false;
        });
}

function pauseMusic() {
    const a = musicRef.value;

    if (!a) return;

    a.pause();
    isPlaying.value = false;
}

function setQuiet(quiet) {
    isQuiet.value = quiet;
    applyVolume();
}

function toggleMusic() {
    if (isPlaying.value) {
        pauseMusic();
        stoppedManually.value = true;
    } else {
        playMusic();
        stoppedManually.value = false;
    }
}

onMounted(() => {
    observer = new IntersectionObserver(
        (entries) => {
            const entry = entries[0];

            if (entry.isIntersecting && !isPlaying.value && !stoppedManually.value) {
                playMusic();
            }
        },
        { threshold: 0.2 }
    );

    if (rootRef.value) {
        observer.observe(rootRef.value);
    }
});

onBeforeUnmount(() => {
    pauseMusic();

    if (observer && rootRef.value) {
        observer.unobserve(rootRef.value);
        observer.disconnect();
    }
});

defineExpose({ pauseMusic, setQuiet });
</script>
<style scoped>
.wave-arch .bar {
    transform-origin: 50% 100%;
    animation: none;
}

.wave-arch .bar.is-playing {
    animation: wavePlay 900ms ease-in-out infinite;
}

@keyframes wavePlay {
    0% {
        transform: scaleY(0.35);
    }
    40% {
        transform: scaleY(1.15);
    }
    60% {
        transform: scaleY(0.8);
    }
    100% {
        transform: scaleY(0.35);
    }
}
</style>
